import { graphql } from "@apollo/client/react/hoc";
import * as React from "react";
import Text from "components/LegacyText";
import LoadingContainer from "components/LoadingContainer";
import FadeIn from "components/Animation/FadeIn";
import profileInfoQuery from "./profileInfoQuery.gql";
import languagesQuery from "./languagesQuery.gql";
import updateMyAssets from "./updateMyAssets.gql";
import updateMyDetails from "./updateMyDetails.gql";
import updateMyProfile from "./updateMyProfile.gql";
import Form, { ProfileMe, Profile as UserProfile } from "./Form";
import { IFile } from "components/Form/FilePicker";
import MissingProfileImg from "../MissingProfileImg";
import Footer from "../Footer";
import { isPinata } from "utilities/flavor";
import { flowRight as compose } from "lodash";

type Asset = IFile & { isPrimary?: boolean };

interface DetailsInput {
  firstName: string;
  lastName: string;
  documents: IFile[];
}

interface ProfileInput {
  profile: UserProfile;
}

interface Props {
  loading: boolean;
  updateMyDetails: (i: DetailsInput) => Promise<any>;
  updateMyProfile: (up: UserProfile | null) => Promise<any>;
  updateMyAssets: (a: Asset[]) => Promise<any>;
  languages: Array<{ key: string; label: string }>;
  error: {} | null;
  me: ProfileMe | null;
  refetch: () => Promise<void>;
}

interface State {
  success: boolean;
  loading: boolean;
  error: boolean;
}

class Profile extends React.Component<Props, State> {
  readonly state: State = {
    loading: false,
    success: false,
    error: false,
  };

  private submit = async ({
    firstName,
    lastName,
    assets,
    profile,
    documents,
  }: DetailsInput & ProfileInput & { assets: Asset[] }) => {
    this.setState({ loading: true, success: false });

    try {
      await Promise.all([
        this.props.updateMyDetails({ firstName, lastName, documents }),
        profile && Object.keys(profile).length > 0 ? this.props.updateMyProfile(profile) : null,
        this.props.updateMyAssets(assets),
      ]);

      await this.props.refetch();
      this.setState({ loading: false, success: true });
    } catch (e) {
      console.error(e);
      this.setState({ loading: false, error: true });
    }
  };

  private renderError({ error }: { error: {} | null }) {
    console.log(error);
    return <Text.Display>Uh oh, something went wrong...try refreshing the page.</Text.Display>;
  }

  private renderPage({ me, languages }: { me: ProfileMe; languages: { key: string; label: string }[] }) {
    const { success, error } = this.state;
    return <Form me={me} languages={languages} onSubmit={this.submit} success={success} error={error} />;
  }

  private renderContent() {
    const { loading, error, me, languages } = this.props;
    const isLoading = !!(loading || this.state.loading || !languages);

    if (isLoading) return <LoadingContainer />;
    if (error) return this.renderError({ error });
    if (!!me) return this.renderPage({ me, languages });
  }

  public render() {
    const { me } = this.props;
    if (!me) return null;
    return (
      <FadeIn>
        {isPinata && <MissingProfileImg />}
        {this.renderContent()}
        <Footer />
      </FadeIn>
    );
  }
}

const ProfileInfo = graphql<any, any, any, any>(profileInfoQuery, {
  props: ({ data: { me, loading, error, refetch } = {} as any }) => {
    if (me) {
      return {
        loading,
        error,
        me,
        refetch,
      };
    } else if (!me && !loading) {
      return {
        loading,
        error,
      };
    } else {
      return {
        loading,
        error,
      };
    }
  },
});

const UpdateMyAssets = graphql<any, any, any, any>(updateMyAssets, {
  props: ({ mutate }) => ({
    updateMyAssets: (assets: any) => mutate && mutate({ variables: { assets } }),
  }),
});

const UpdateMyDetails = graphql<any, any, any, any>(updateMyDetails, {
  props: ({ mutate }) => ({
    updateMyDetails: ({ firstName, lastName, phone, documents }: any) => {
      return mutate && mutate({ variables: { input: { firstName, lastName, phone, documents } } });
    },
  }),
});

const UpdateMyProfile = graphql<any, any, any, any>(updateMyProfile, {
  props: ({ mutate }) => ({
    updateMyProfile: (props: any) => {
      const { selfSummary, languages, shirtSize, birthday } = props;
      let addressFields = {};
      return (
        mutate &&
        mutate({
          variables: {
            input: {
              selfSummary,
              shirtSize,
              languageKeys: (languages || []).map(({ key }: { key: string }) => key),
              birthday,
              ...addressFields,
            },
          },
        })
      );
    },
  }),
});

const Languages = graphql<any, any, any, any>(languagesQuery, {
  props: ({ data: { options: languages } = {} as any }) => ({ languages }),
});

export default compose(ProfileInfo, UpdateMyAssets, UpdateMyDetails, UpdateMyProfile, Languages)(Profile);
